import { useState, useCallback, useMemo } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, Badge } from '@devkit/ui';
import { Braces, ChevronRight, ChevronDown, Search, X } from 'lucide-react';
import { buildJsonTree, type JsonTreeNode } from '@devkit/tools-json';
import { trackToolExecute, trackClear } from '@devkit/analytics';

const defaultJson = `{
  "name": "DevKit",
  "version": "1.0.0",
  "private": true,
  "scripts": {
    "dev": "vite",
    "build": "tsc && vite build",
    "preview": "vite preview"
  },
  "dependencies": {
    "react": "^18.2.0",
    "vite": "^5.0.0"
  },
  "tags": ["json", "tools", "developer"]
}`;

const TYPE_COLORS: Record<string, string> = {
  object: 'text-amber-400 bg-amber-500/10',
  array: 'text-purple-400 bg-purple-500/10',
  string: 'text-green-400 bg-green-500/10',
  number: 'text-blue-400 bg-blue-500/10',
  boolean: 'text-pink-400 bg-pink-500/10',
  null: 'text-slate-400 bg-slate-500/10',
};

function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-500/30 text-yellow-100 rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function TreeNode({
  node,
  query,
  expanded,
  toggle,
}: {
  node: JsonTreeNode;
  query: string;
  expanded: Set<string>;
  toggle: (key: string) => void;
}) {
  const hasChildren = !!node.children && node.children.length > 0;
  const isOpen = expanded.has(node.key);
  const matches =
    !query.trim() ||
    node.label.toLowerCase().includes(query.toLowerCase()) ||
    node.preview.toLowerCase().includes(query.toLowerCase()) ||
    node.path.toLowerCase().includes(query.toLowerCase());

  if (!matches) return null;

  const colorClass = TYPE_COLORS[node.type] ?? TYPE_COLORS.null;

  return (
    <div>
      <div
        className="flex items-start gap-1 hover:bg-surface/50 rounded px-1 py-0.5 group"
        style={{ paddingLeft: `${node.depth * 16 + 4}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => toggle(node.key)}
            className="flex-shrink-0 mt-0.5 text-slate-400 hover:text-white"
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}

        <span className="text-xs text-slate-500 flex-shrink-0 font-mono">
          {node.type === 'array' ? `[${node.label}]` : node.label}
          <span className="text-slate-600">:</span>
        </span>

        <span
          className={`text-xs font-medium px-1.5 py-0.5 rounded ${colorClass} flex-shrink-0`}
        >
          {node.type}
        </span>

        <span className="text-xs font-mono text-slate-300 truncate min-w-0">
          {highlight(node.preview, query)}
        </span>
      </div>

      {hasChildren && isOpen && (
        <div>
          {node.children!.map((child) => (
            <TreeNode
              key={child.key}
              node={child}
              query={query}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function JsonViewerPage() {
  const [input, setInput] = useState(defaultJson);
  const [error, setError] = useState<string | null>(null);
  const [tree, setTree] = useState<JsonTreeNode | null>(null);
  const [stats, setStats] = useState<{ nodeCount: number; maxDepth: number } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const handleParse = useCallback(() => {
    setError(null);
    const result = buildJsonTree(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to parse JSON.');
      setTree(null);
      setStats(null);
      return;
    }
    setTree(result.data);
    setStats({ nodeCount: result.nodeCount ?? 0, maxDepth: result.maxDepth ?? 0 });

    // Expand all nodes by default.
    const allKeys = new Set<string>();
    const walk = (n: JsonTreeNode) => {
      allKeys.add(n.key);
      n.children?.forEach(walk);
    };
    walk(result.data);
    setExpanded(allKeys);
    trackToolExecute('json-viewer', 'parse');
  }, [input]);

  const clearAll = useCallback(() => {
    setInput('');
    setTree(null);
    setStats(null);
    setError(null);
    setSearch('');
    trackClear('json-viewer');
  }, []);

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (!tree) return;
    const allKeys = new Set<string>();
    const walk = (n: JsonTreeNode) => {
      allKeys.add(n.key);
      n.children?.forEach(walk);
    };
    walk(tree);
    setExpanded(allKeys);
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpanded(new Set([tree?.key ?? '']));
  }, [tree]);

  const filteredCount = useMemo(() => {
    if (!tree || !search.trim()) return stats?.nodeCount ?? 0;
    let count = 0;
    const walk = (n: JsonTreeNode) => {
      const matches =
        n.label.toLowerCase().includes(search.toLowerCase()) ||
        n.preview.toLowerCase().includes(search.toLowerCase()) ||
        n.path.toLowerCase().includes(search.toLowerCase());
      if (matches) count++;
      n.children?.forEach(walk);
    };
    walk(tree);
    return count;
  }, [tree, search, stats]);

  return (
    <ToolLayout slug="json-viewer">
      <Card>
        <CardContent className="p-6">
          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Paste JSON
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your JSON here..."
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handleParse} className="gap-2">
              <Braces className="w-4 h-4" />
              Build Tree
            </Button>
            <Button variant="outline" onClick={expandAll} disabled={!tree}>
              Expand All
            </Button>
            <Button variant="outline" onClick={collapseAll} disabled={!tree}>
              Collapse All
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400 font-medium">Error</p>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          )}

          {/* Tree View */}
          {tree && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-slate-700">
                <Badge variant="default">{stats?.nodeCount} nodes</Badge>
                <Badge variant="default">Depth: {stats?.maxDepth}</Badge>
                <div className="flex-1 min-w-[180px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search keys or values..."
                    className="w-full pl-9 pr-9 py-2 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {search && (
                  <span className="text-sm text-slate-400">
                    {filteredCount} match{filteredCount !== 1 ? 'es' : ''}
                  </span>
                )}
              </div>

              <div className="bg-surface/40 border border-slate-700/50 rounded-lg p-3 max-h-[600px] overflow-auto">
                <TreeNode node={tree} query={search} expanded={expanded} toggle={toggle} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
